import { useState } from "react"
import OutsideClickHandler from "react-outside-click-handler"
import classNames from "classnames"

import { Field, ValueView } from "web/components"
import { useFormContext } from "web/components/Form/Form"
import { isInputableField } from "web/components/Form/helpers"
import { BooleanSelect } from "web/components/Form/Controls"
import Label from "web/components/Form/Controls/Label"
import ValueWithLabel from "./ValueWithLabel"

import { isAValue, isObjectWithProperty } from "web/services/helpers"

import { useCurrentUser } from "web/hooks/data"

export default function ViewOrEditField({
  fieldKey,
  data,
  readOnly = false,
  showAll = false,
  mode: modeFromProps,
  hideLabel,
  valueLocked,
  openLockedFieldEditModal,
  isModalEditable,
  openEditModal,
  ...restOfProps
}) {
  const form = useFormContext()
  const [isEditing, setIsEditing] = useState(false)

  const currentUser = useCurrentUser()

  const field = form.fields.find((el) => el.key === fieldKey)
  if (!field.isVisible) return null

  const isRequired = field.isMandatoryRequired || field.isRequired

  const value = isObjectWithProperty(data, field.key)
    ? data[field.key]
    : { value: form.value[field.key], form_value: form.value[field.key] }

  const fieldCanBeEdited =
    isInputableField(field) &&
    !readOnly &&
    !field.isReadOnly &&
    (!valueLocked || currentUser.is_data_admin)

  const showInput =
    fieldCanBeEdited &&
    !valueLocked &&
    (modeFromProps === "edit" || isEditing) &&
    (!isModalEditable || !isModalEditable(fieldKey))

  const canEdit = fieldCanBeEdited && modeFromProps !== "view"

  function handleValueClick() {
    if (valueLocked && currentUser.is_data_admin) {
      openLockedFieldEditModal(fieldKey, field)
    } else if (isModalEditable && isModalEditable(fieldKey)) {
      openEditModal(fieldKey)
    } else if (!valueLocked) {
      setIsEditing(true)
    }
  }

  function handleFieldBlur() {
    if (isEditing) {
      setTimeout(() => setIsEditing(false), 1)
    }
  }

  const controlOverride =
    field.kind === "Boolean" ? { Control: BooleanSelect } : {}

  return (
    <div
      className={classNames("view-or-edit-field", {
        mandatory: isRequired,
        optional: field.isOptionalRequired,
      })}
    >
      {showInput ? (
        <OutsideClickHandler onOutsideClick={handleFieldBlur}>
          <div
            className={classNames("view-or-edit-input-row", {
              "flex items-start gap-4": !hideLabel,
            })}
          >
            {!hideLabel && (
              <Label
                kind={field.kind}
                label={field.label}
                description={field.description}
                required={isRequired}
                variant="inline"
              />
            )}
            <div className="min-w-0 basis-1/2">
              <Field
                fieldKey={field.key}
                autoFocus={isEditing}
                onBlur={handleFieldBlur}
                hideLabel={true}
                {...controlOverride}
                {...restOfProps}
              />
              <FieldSubText value={value} field={field} showAll={showAll} />
            </div>
          </div>
        </OutsideClickHandler>
      ) : (
        <>
          <ValueWithLabel
            fieldKey={field.key}
            kind={field.kind}
            label={field.label}
            description={field.description}
            required={isRequired}
            value={value}
            onClick={canEdit && handleValueClick}
            showLinkIcon={canEdit}
            hideLabel={hideLabel}
            valueLocked={valueLocked}
            currentUserDataAdmin={currentUser.is_data_admin}
            inEditMode={modeFromProps === "edit"}
            readOnly={readOnly}
          />
          <FieldSubText value={value} field={field} showAll={showAll} />
        </>
      )}
    </div>
  )
}

function FieldSubText({ value, field, showAll }) {
  const fieldHasFormValue = isAValue(value.form_value)
  const fieldHasDisplayValue = isAValue(value.value)

  const showDisplayValueForFieldWithoutFormValue =
    !fieldHasFormValue &&
    fieldHasDisplayValue &&
    !value.is_calculated && // display value is not calculated
    showAll // in show all mode

  if (!showDisplayValueForFieldWithoutFormValue) return null

  return (
    <div className="pt-1 text-sm text-green-700">
      <ValueView fieldKey={field.key} {...value} has_more_info={true} />
    </div>
  )
}
